import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Palette, Rocket, Folder, Tag, Hand, PanelTop, Bell, AlarmClock, BookOpen, MessagesSquare, PenSquare, Send, Reply, FileText,
  Zap, ShieldCheck, Ban, CalendarClock, Sparkles, PenLine as Signature, Users, Mail, CalendarDays, ListTodo, ChevronLeft, ChevronRight, X, Plus, Trash2, Pencil,
  Eye, EyeOff, Search, Settings as Cog, LogOut, Check, Loader2, ListFilter, ChevronUp, ChevronDown,
} from 'lucide-react';
import api from '../../utils/api';
import { Button, Field, Input, Select, Switch, errMsg } from '../SupportCoordination/kit';
import MailEditor from './MailEditor';
import { RulesSection, QuickStepsSection } from './RulesQuickSteps';
import { avatarColor, initials, fullTime, listTime } from './mailUi';

// Rostery Mail settings, in the order Outlook and the big mail apps use. Most choices are each
// person's own and save as soon as they change; signatures, templates and quick texts, tags,
// blocked senders and automatic replies have their own lists.
const SECTIONS = [
  { group: 'Preferences', items: [
    ['appearance', 'Appearance', Palette], ['startup', 'Show on startup', Rocket], ['folders', 'Folders', Folder], ['tags', 'Tags', Tag],
    ['toolbar', 'Toolbar', PanelTop], ['swipe', 'Swipe', Hand], ['notifications', 'Notifications', Bell], ['reminders', 'Reminders', AlarmClock],
  ] },
  { group: 'Mail', items: [
    ['read', 'Reading', BookOpen], ['conversations', 'Conversations', MessagesSquare], ['compose', 'Compose', PenSquare], ['send', 'Send', Send],
    ['rules', 'Rules', ListFilter], ['quicksteps', 'Quick steps', Zap], ['replies', 'Replies and forwards', Reply], ['signatures', 'Signatures', Signature], ['templates', 'Templates', FileText], ['quicktexts', 'Quick texts', Zap],
    ['privacy', 'Privacy and pictures', ShieldCheck], ['blocked', 'Blocked and safe senders', Ban], ['autoreply', 'Automatic replies', CalendarClock], ['ai', 'AI assistant', Sparkles],
  ] },
  { group: 'General', items: [['accounts', 'Mailboxes', Mail], ['contacts', 'Contacts', Users], ['calendar', 'Calendar', CalendarDays], ['tasks', 'Tasks and notes', ListTodo]] },
];
const ALL = SECTIONS.flatMap((g) => g.items);

export default function MailSettings({ prefs, savePrefs, accounts, me, tags, reloadTags, reloadAccounts, onClose, onAdd, onAccountSettings, onCompose, onOpenMessage, startAt, aiAllowed = true, ruleDraft = null, onQuickStepsChanged, scOn = true, pmOn = true, tasksOn = true }) {
  const [section, setSection] = useState(startAt || null);
  useEffect(() => { if (startAt) setSection(startAt); }, [startAt]);
  const [q, setQ] = useState('');
  const narrow = typeof window !== 'undefined' && window.innerWidth < 768;
  const current = ALL.find((x) => x[0] === section) || (narrow ? null : ALL[0]);
  const props = { prefs, savePrefs, accounts, me, tags, reloadTags, reloadAccounts, onAdd, onAccountSettings, onCompose, onOpenMessage, aiAllowed, ruleDraft, onQuickStepsChanged, scOn, pmOn, tasksOn };
  const match = (label) => !q || label.toLowerCase().includes(q.toLowerCase());
  return (
    <div className="flex flex-1 min-h-0 bg-white">
      <nav className={clsx('w-full md:w-[260px] flex-shrink-0 flex-col border-r border-slate-200 bg-[#fafafa] min-h-0', current && narrow ? 'hidden' : 'flex')} aria-label="Mail settings">
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <button type="button" onClick={onClose} aria-label="Back to mail" className="w-9 h-9 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-200/60"><X size={18} /></button>
          <h2 className="text-[18px] font-semibold tracking-tight">Settings</h2>
        </div>
        <div className="px-3 pb-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search settings" aria-label="Search settings"
              className="w-full h-8 pl-8 pr-2 rounded-md bg-white ring-1 ring-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0f6cbd]/60" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto mail-scroll px-2 pb-4">
          {SECTIONS.map((g) => {
            const items = g.items.filter(([, label]) => match(label));
            if (!items.length) return null;
            return (
              <div key={g.group} className="mt-2">
                <p className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{g.group}</p>
                {items.map(([key, label, Icon]) => (
                  <button key={key} type="button" onClick={() => setSection(key)} aria-current={current && current[0] === key ? 'page' : undefined}
                    className={clsx('relative w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-[13.5px] text-left',
                      current && current[0] === key ? 'bg-sky-100/80 font-semibold text-slate-900' : 'text-slate-700 hover:bg-slate-200/50')}>
                    {current && current[0] === key && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#0f6cbd]" aria-hidden />}
                    <Icon size={16} className={current && current[0] === key ? 'text-[#0f6cbd]' : 'text-slate-500'} aria-hidden />
                    <span className="flex-1">{label}</span>
                    <ChevronRight size={14} className="md:hidden text-slate-400" aria-hidden />
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </nav>
      {current && (
        <section className="flex-1 min-w-0 flex flex-col min-h-0">
          <header className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-slate-100">
            {narrow && <button type="button" onClick={() => setSection(null)} aria-label="All settings" className="w-9 h-9 -ml-2 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100"><ChevronLeft size={18} /></button>}
            <h3 className="text-[17px] font-semibold tracking-tight">{current[1]}</h3>
          </header>
          <div className="flex-1 min-h-0 overflow-y-auto mail-scroll px-5 py-5">
            <div className="max-w-2xl">
              <Section id={current[0]} {...props} />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Building blocks ───────────────────────────────────────────────────────────────────────────
function Choice({ label, hint, value, onChange, options }) {
  return (
    <div className="py-3.5 border-b border-slate-100 last:border-0">
      <p className="text-[13.5px] font-semibold text-slate-800">{label}</p>
      {hint && <p className="text-[12.5px] text-slate-500 mt-0.5">{hint}</p>}
      <div className="flex flex-wrap gap-2 mt-2.5" role="radiogroup" aria-label={label}>
        {options.map(([v, l, sub]) => (
          <button key={String(v)} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}
            className={clsx('rounded-lg px-3 py-2 text-left ring-1 text-[13px] transition-colors', value === v ? 'ring-[#0f6cbd] bg-sky-50 text-slate-900 font-semibold' : 'ring-slate-200 text-slate-700 hover:bg-slate-50')}>
            {l}{sub && <span className="block text-[11.5px] font-normal text-slate-500">{sub}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
function Toggle({ label, hint, checked, onChange }) {
  return <div className="py-3.5 border-b border-slate-100 last:border-0"><Switch label={label} hint={hint} checked={checked} onChange={onChange} /></div>;
}
const Note = ({ children }) => <p className="text-[12.5px] text-slate-600 bg-slate-50 rounded-lg px-3 py-2.5 leading-relaxed">{children}</p>;
const Empty = ({ children }) => <p className="text-[13px] text-slate-500 py-6 text-center">{children}</p>;

function Section({ id, prefs, savePrefs, ...rest }) {
  const set = (patch) => savePrefs(patch);
  const p = prefs;
  switch (id) {
    case 'appearance': return (
      <>
        <Choice label="Density" hint="How much space each message takes in the list." value={p.appearance.density} onChange={(v) => set({ appearance: { ...p.appearance, density: v } })}
          options={[['roomy', 'Roomy', 'More space, easier to tap'], ['compact', 'Compact', 'More mail on the screen']]} />
        <Choice label="Reading pane" value={p.appearance.readingPane} onChange={(v) => set({ appearance: { ...p.appearance, readingPane: v } })}
          options={[['right', 'On the right'], ['bottom', 'Below the list'], ['off', 'Off', 'Open mail full width']]} />
        <Choice label="Message preview" hint="Lines of text under each subject." value={p.appearance.preview} onChange={(v) => set({ appearance: { ...p.appearance, preview: v } })}
          options={[[0, 'None'], [1, 'One line'], [2, 'Two lines']]} />
        <Choice label="Text size" value={p.appearance.fontSize} onChange={(v) => set({ appearance: { ...p.appearance, fontSize: v } })}
          options={[['small', 'Small'], ['normal', 'Normal'], ['large', 'Large']]} />
        <Toggle label="Show sender pictures" hint="The coloured circle with the sender's initials." checked={p.appearance.avatars} onChange={(v) => set({ appearance: { ...p.appearance, avatars: v } })} />
        <Choice label="Zoom" hint="How big an open message shows. Zoom in and out on the View tab too." value={p.appearance.zoom || 100} onChange={(v) => set({ appearance: { ...p.appearance, zoom: v } })}
          options={[80, 90, 100, 110, 125, 150].map((z) => [z, `${z}%`])} />
        <Choice label="Ribbon" hint="The Home and View tabs above your mail, on a computer." value={p.appearance.ribbon || 'classic'} onChange={(v) => set({ appearance: { ...p.appearance, ribbon: v } })}
          options={[['classic', 'Classic', 'Big buttons with names'], ['simple', 'Simple', 'One line of small buttons']]} />
        <Toggle label="Show the folder pane" hint="The folder list on the left, on a wide screen. When it is off, the folder button above the list opens it." checked={p.appearance.folderPane !== false} onChange={(v) => set({ appearance: { ...p.appearance, folderPane: v } })} />
      </>
    );
    case 'startup': return (
      <Choice label="When Rostery Mail opens, show" value={p.startup} onChange={(v) => set({ startup: v })}
        options={[['inbox', 'Inbox'], ['unread', 'Unread'], ['flagged', 'Flagged'], ['waiting', 'Waiting for reply'], ['snoozed', 'Snoozed'], rest.scOn && ['sc', 'Support Coordination'], rest.pmOn && ['pm', 'Plan Management'], ['last', 'Where I left off']].filter(Boolean)} />
    );
    case 'folders': return <FoldersSection prefs={p} set={set} {...rest} />;
    case 'tags': return <TagsSection {...rest} />;
    case 'toolbar': return <ToolbarSection prefs={p} set={set} />;
    case 'swipe': return (
      <>
        <Note>On a touch screen (a phone or tablet), swipe a message in the list to act on it at once.</Note>
        {['left', 'right'].map((side) => (
          <Choice key={side} label={`Swipe ${side}`} value={p.swipe[side]} onChange={(v) => set({ swipe: { ...p.swipe, [side]: v } })}
            options={[['archive', 'Archive'], ['delete', 'Delete'], ['flag', 'Flag'], ['read', 'Read / unread'], ['none', 'Nothing']]} />
        ))}
      </>
    );
    case 'notifications': return <NotificationsSection prefs={p} set={set} />;
    case 'reminders': return <RemindersSection {...rest} />;
    case 'read': return (
      <>
        <Choice label="Mark mail as read" value={p.read.markRead} onChange={(v) => set({ read: { ...p.read, markRead: v } })}
          options={[['open', 'When I open it'], ['delay', 'After a few seconds'], ['never', 'Only when I choose']]} />
        {p.read.markRead === 'delay' && (
          <Choice label="After" value={p.read.delaySec} onChange={(v) => set({ read: { ...p.read, delaySec: v } })} options={[[1, '1 second'], [3, '3 seconds'], [5, '5 seconds'], [10, '10 seconds'], [30, '30 seconds']]} />
        )}
        <Toggle label="Check links before they open" hint="Shows the real web address of a link in a message and asks first. Helps spot scam links." checked={p.read.confirmLinks} onChange={(v) => set({ read: { ...p.read, confirmLinks: v } })} />
      </>
    );
    case 'conversations': return (
      <>
        <Toggle label="Group messages into conversations" hint="A message and its replies show as one line in the list, and together when opened." checked={p.conversations} onChange={(v) => set({ conversations: v })} />
        <Note>Microsoft and Google group conversations themselves; for other mailboxes Rostery groups replies by their subject.</Note>
      </>
    );
    case 'compose': return <ComposeSection prefs={p} set={set} accounts={rest.accounts} />;
    case 'send': return (
      <>
        <Choice label="Undo send" hint="Hold each message for a moment after you press Send, so you can take it back." value={p.send.undoSeconds} onChange={(v) => set({ send: { ...p.send, undoSeconds: v } })}
          options={[[0, 'Off'], [5, '5 seconds'], [10, '10 seconds'], [20, '20 seconds'], [30, '30 seconds']]} />
        <Toggle label="Ask for a read receipt on every message" hint="The person's mail app may ask them first, or not send one at all." checked={p.send.readReceipt} onChange={(v) => set({ send: { ...p.send, readReceipt: v } })} />
        <Note>To send at a later time, press the arrow next to Send. Mail waiting to go is listed under Reminders.</Note>
      </>
    );
    case 'replies': return (
      <>
        <Choice label="Main reply button" value={p.replies.defaultReply} onChange={(v) => set({ replies: { ...p.replies, defaultReply: v } })}
          options={[['reply', 'Reply', 'To the sender'], ['replyAll', 'Reply all', 'To everyone on it']]} />
        <Toggle label="Include the original message" hint="Adds the message you are answering under your reply." checked={p.replies.includeOriginal} onChange={(v) => set({ replies: { ...p.replies, includeOriginal: v } })} />
      </>
    );
    case 'signatures': return <SnippetsSection kind="signature" prefs={p} set={set} {...rest} />;
    case 'templates': return <SnippetsSection kind="template" prefs={p} set={set} {...rest} />;
    case 'quicktexts': return <SnippetsSection kind="quicktext" prefs={p} set={set} {...rest} />;
    case 'privacy': return (
      <>
        <Choice label="Pictures from the internet" hint="Senders can tell when you open a message by the pictures it loads." value={p.read.pictures} onChange={(v) => set({ read: { ...p.read, pictures: v } })}
          options={[['ask', 'Ask each time', 'Recommended'], ['safe', 'Show for safe senders'], ['always', 'Always show']]} />
        <Note>Tracking pictures (the invisible 1×1 kind, and known tracking services) are always removed, even when pictures are shown. Messages show how many were blocked.</Note>
      </>
    );
    case 'rules': return <RulesSection accounts={rest.accounts} tags={rest.tags} draft={rest.ruleDraft} me={rest.me} />;
    case 'quicksteps': return <QuickStepsSection accounts={rest.accounts} tags={rest.tags} onChanged={rest.onQuickStepsChanged} />;
    case 'blocked': return <SendersSection />;
    case 'autoreply': return <AutoReplySection {...rest} />;
    case 'ai': return !rest.aiAllowed ? <Note>The AI assistant is not switched on for your company yet. Ask Rostery support to turn on AI features.</Note> : (
      <>
        <Toggle label="AI assistant" hint="Summarise conversations, draft replies, improve what you write and translate. Mail text is sent to the AI only when you press an AI button." checked={p.ai.enabled} onChange={(v) => set({ ai: { ...p.ai, enabled: v } })} />
        <div className="py-3.5">
          <Field label="Translate into"><Select value={p.ai.language} onChange={(v) => set({ ai: { ...p.ai, language: v } })}
            options={['English', 'Arabic', 'Chinese (Simplified)', 'Vietnamese', 'Italian', 'Greek', 'Hindi', 'Punjabi', 'Urdu', 'Spanish', 'Korean', 'Filipino'].map((x) => ({ value: x, label: x }))} /></Field>
        </div>
      </>
    );
    case 'accounts': return <AccountsSection {...rest} />;
    case 'contacts': return <ContactsSection {...rest} />;
    case 'calendar': return rest.scOn ? (
      <>
        <Note>Meeting invites in your mail show as a card with <b>Add to my calendar</b>, which puts them in the Support Coordination calendar (linked to the participant when the mail is about one).</Note>
        <div className="mt-4"><Link to="/support-coordination?view=calendar" className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-[#0f6cbd] text-white text-[13px] font-semibold"><CalendarDays size={15} aria-hidden /> Open the calendar</Link></div>
      </>
    ) : <Note>Meeting invites in your mail show as a card with the time and place. A calendar inside Email is on its way.</Note>;
    case 'tasks': return (
      <>
        <Note>
          From any message:{rest.tasksOn ? <> <b>Create task</b> makes a Rostery task (with the participant when the mail is about one).</> : ' '}
          {rest.scOn && <> <b>Log time / case note</b> opens Support Coordination's time entry with the email as the note.</>}
          {' '}<b>Remind me</b> brings a message back to you on a day you choose.
        </Note>
        {rest.tasksOn && <div className="mt-4 flex gap-2"><Link to="/tasks" className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg ring-1 ring-slate-200 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><ListTodo size={15} aria-hidden /> Open Tasks</Link></div>}
      </>
    );
    default: return null;
  }
}

// ── Folders: show / hide, new, rename, remove ────────────────────────────────────────────────
function FoldersSection({ prefs, set, accounts, reloadAccounts }) {
  const hidden = new Set(prefs.hiddenFolders || []);
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(null);
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState(null);
  const FIXED = ['inbox', 'sent', 'drafts', 'trash', 'junk', 'archive'];
  const toggle = (id) => { const n = new Set(hidden); if (n.has(id)) n.delete(id); else n.add(id); set({ hiddenFolders: [...n] }); };
  const run = async (key, fn, ok) => { setBusy(key); try { await fn(); toast.success(ok); await reloadAccounts(); return true; } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(null); } };
  if (!(accounts || []).length) return <Empty>No mailboxes yet.</Empty>;
  return (
    <div className="space-y-6">
      <Note>Hide folders you never use from the folder list. New, renamed and removed folders change in the mailbox itself too.</Note>
      {accounts.map((a) => (
        <div key={a.id}>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full" style={{ background: a.color || avatarColor(a.email) }} aria-hidden />
            <p className="text-[13.5px] font-semibold text-slate-800 flex-1 truncate">{a.email}</p>
            {a.can_act && <Button size="sm" variant="secondary" icon={Plus} onClick={() => { setAdding(a.id); setName(''); }}>New folder</Button>}
          </div>
          {adding === a.id && (
            <form className="flex gap-2 mb-2" onSubmit={(e) => { e.preventDefault(); run('new', () => api.post('/mail/folders', { account_id: a.id, name }), 'Folder made').then((done) => done && setAdding(null)); }}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Folder name" autoFocus aria-label="New folder name" />
              <Button type="submit" loading={busy === 'new'} disabled={!name.trim()}>Add</Button>
              <Button variant="ghost" onClick={() => setAdding(null)}>Cancel</Button>
            </form>
          )}
          <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
            {a.folders.map((f) => (
              <li key={f.id} className="flex items-center gap-2 px-3 py-2">
                <Folder size={15} className="text-slate-400 flex-shrink-0" aria-hidden />
                {renaming === f.id ? (
                  <form className="flex-1 flex gap-2" onSubmit={(e) => { e.preventDefault(); run(f.id, () => api.patch(`/mail/folders/${f.id}`, { name }), 'Renamed').then((done) => done && setRenaming(null)); }}>
                    <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus aria-label="Folder name" />
                    <Button type="submit" size="sm" loading={busy === f.id}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>Cancel</Button>
                  </form>
                ) : (
                  <>
                    <span className={clsx('flex-1 truncate text-[13px]', hidden.has(f.id) ? 'text-slate-400 line-through' : 'text-slate-800')}>{f.path || f.name}</span>
                    <span className="text-[11.5px] text-slate-400 tabular-nums">{f.total || 0}</span>
                    <IconAct label={hidden.has(f.id) ? 'Show in the list' : 'Hide from the list'} icon={hidden.has(f.id) ? EyeOff : Eye} onClick={() => toggle(f.id)} />
                    {a.can_act && !FIXED.includes(f.role) && <IconAct label="Rename" icon={Pencil} onClick={() => { setRenaming(f.id); setName(f.name); }} />}
                    {a.can_act && !FIXED.includes(f.role) && <IconAct label="Remove" icon={Trash2} danger onClick={() => { if (window.confirm(`Remove the folder "${f.name}" and the mail in it, in the mailbox too?`)) run(f.id, () => api.delete(`/mail/folders/${f.id}`), 'Folder removed'); }} />}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
const IconAct = ({ label, icon: Icon, onClick, danger }) => (
  <button type="button" onClick={onClick} title={label} aria-label={label} className={clsx('w-8 h-8 rounded-md flex items-center justify-center', danger ? 'text-slate-500 hover:bg-rose-50 hover:text-rose-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800')}><Icon size={15} aria-hidden /></button>
);

// ── Tags ──────────────────────────────────────────────────────────────────────────────────────
const TAG_COLOURS = ['#d13438', '#ca5010', '#986f0b', '#498205', '#038387', '#0f6cbd', '#8764b8', '#c239b3', '#69797e'];
function TagsSection({ tags, reloadTags, me }) {
  const [form, setForm] = useState(null);
  const save = async () => {
    try {
      if (form.id) await api.put(`/mail/tags/${form.id}`, form); else await api.post('/mail/tags', form);
      toast.success('Saved'); setForm(null); reloadTags();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Remove the tag "${t.name}" from every message?`)) return;
    try { await api.delete(`/mail/tags/${t.id}`); reloadTags(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Note>Tags are shared by your company: anyone can tag a message, and filter by a tag in the folder list. They stay in Rostery and do not change the mailbox.</Note>
      <div className="mt-4 flex justify-end"><Button size="sm" icon={Plus} onClick={() => setForm({ name: '', color: TAG_COLOURS[(tags || []).length % TAG_COLOURS.length] })}>New tag</Button></div>
      {form && <TagForm form={form} setForm={setForm} onSave={save} />}
      {(tags || []).length ? (
        <ul className="mt-3 rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
          {tags.map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2.5">
              <span className="w-3 h-3 rounded-full" style={{ background: t.color }} aria-hidden />
              <span className="flex-1 text-[13.5px] text-slate-800">{t.name}</span>
              {(['admin', 'super_admin'].includes(me.role) || t.created_by === me.id) && (
                <><IconAct label="Change" icon={Pencil} onClick={() => setForm(t)} /><IconAct label="Remove" icon={Trash2} danger onClick={() => remove(t)} /></>
              )}
            </li>
          ))}
        </ul>
      ) : !form && <Empty>No tags yet. Make one such as "Urgent", "Plan review" or "Waiting on NDIA".</Empty>}
    </>
  );
}
function TagForm({ form, setForm, onSave }) {
  return (
    <form className="mt-3 rounded-xl ring-1 ring-sky-200 bg-sky-50/40 p-3.5 space-y-3" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
      <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus maxLength={40} /></Field>
      <div className="flex gap-2" role="radiogroup" aria-label="Colour">
        {TAG_COLOURS.map((c) => <button key={c} type="button" role="radio" aria-checked={form.color === c} aria-label={c} onClick={() => setForm({ ...form, color: c })} className={clsx('w-7 h-7 rounded-full ring-offset-2', form.color === c && 'ring-2 ring-slate-800')} style={{ background: c }} />)}
      </div>
      <div className="flex gap-2 justify-end"><Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button><Button type="submit" disabled={!form.name.trim()}>Save</Button></div>
    </form>
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────────────────────────
const TOOLBAR = [['reply', 'Reply'], ['replyAll', 'Reply all'], ['forward', 'Forward'], ['archive', 'Archive'], ['delete', 'Delete'], ['move', 'Move'], ['junk', 'Junk'], ['flag', 'Flag'], ['unread', 'Mark unread'],
  ['tag', 'Tag'], ['remind', 'Remind me'], ['task', 'Create task'], ['logtime', 'Log time / case note'], ['ai', 'AI'], ['translate', 'Translate'], ['print', 'Print']];
// On a computer these are on the ribbon above the mail, so the toolbar there leaves them out.
const ON_RIBBON = ['archive', 'delete', 'move', 'junk', 'flag', 'unread', 'tag', 'print'];
function ToolbarSection({ prefs, set }) {
  const on = prefs.toolbar || [];
  const move = (k, d) => { const i = on.indexOf(k); const n = [...on]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; set({ toolbar: n }); };
  return (
    <>
      <Note>Choose the buttons above an open message, and their order. Anything not chosen is under the ⋯ menu. On a computer, the buttons marked "Ribbon" sit on the ribbon at the top (Home tab) instead, so for those these choices apply on a phone.</Note>
      <ul className="mt-4 rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
        {[...on.map((k) => TOOLBAR.find((t) => t[0] === k)).filter(Boolean), ...TOOLBAR.filter((t) => !on.includes(t[0]))].map(([k, label]) => (
          <li key={k} className="flex items-center gap-3 px-3 py-2">
            <input type="checkbox" checked={on.includes(k)} onChange={() => set({ toolbar: on.includes(k) ? on.filter((x) => x !== k) : [...on, k] })} aria-label={`Show ${label}`} className="w-4 h-4 accent-[#0f6cbd]" />
            <span className={clsx('flex-1 text-[13.5px]', on.includes(k) ? 'text-slate-800' : 'text-slate-500')}>{label}</span>
            {ON_RIBBON.includes(k) && <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5" title="On a computer this is on the ribbon">Ribbon</span>}
            {on.includes(k) && <><IconAct label="Move up" icon={ChevronUp} onClick={() => move(k, -1)} /><IconAct label="Move down" icon={ChevronDown} onClick={() => move(k, 1)} /></>}
          </li>
        ))}
      </ul>
    </>
  );
}

// ── Notifications ─────────────────────────────────────────────────────────────────────────────
function NotificationsSection({ prefs, set }) {
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [perm, setPerm] = useState(supported ? Notification.permission : 'unsupported');
  const enable = async (v) => {
    if (v && supported && Notification.permission !== 'granted') {
      const r = await Notification.requestPermission();
      setPerm(r);
      if (r !== 'granted') { toast.error('Your browser blocked notifications. Allow them for this site in the browser settings.'); return; }
    }
    set({ notifications: { ...prefs.notifications, desktop: v } });
  };
  return (
    <>
      <Toggle label="Show a notification for new mail" hint="While Rostery Mail is open in a browser tab and you are in another tab or window. Rostery checks for new mail every minute." checked={prefs.notifications.desktop && perm === 'granted'} onChange={enable} />
      <Toggle label="Play a sound" hint="When new mail comes in while Rostery Mail is open." checked={prefs.notifications.sound} onChange={(v) => set({ notifications: { ...prefs.notifications, sound: v } })} />
      {perm === 'denied' && <Note>Notifications are blocked for this site. Allow them in your browser's site settings, then turn this on again.</Note>}
      {!supported && <Note>This browser cannot show notifications.</Note>}
    </>
  );
}

// ── Reminders: watch for reply / remind me, and mail waiting to go ───────────────────────────
function RemindersSection({ onOpenMessage }) {
  const [watch, setWatch] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const load = useCallback(() => {
    api.get('/mail/watch').then((r) => setWatch(r.data || [])).catch(() => setWatch([]));
    api.get('/mail/outbox').then((r) => setOutbox(r.data || [])).catch(() => setOutbox([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = async (id) => { await api.delete(`/mail/watch/${id}`).catch(() => {}); load(); };
  const cancel = async (id) => { try { await api.post(`/mail/outbox/${id}/cancel`); toast.success('Not sent'); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!watch) return <Loader2 size={18} className="animate-spin text-slate-400" aria-label="Loading" />;
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[13.5px] font-semibold text-slate-800 mb-2">Watching for a reply, and reminders</p>
        {watch.length ? (
          <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
            {watch.map((w) => (
              <li key={w.id} className="flex items-center gap-3 px-3 py-2.5">
                <AlarmClock size={15} className={w.status === 'due' ? 'text-rose-600' : 'text-slate-400'} aria-hidden />
                <button type="button" onClick={() => onOpenMessage && onOpenMessage(w.message_id)} className="flex-1 min-w-0 text-left">
                  <span className="block text-[13px] text-slate-800 truncate">{w.subject || '(no subject)'}</span>
                  <span className={clsx('block text-[11.5px]', w.status === 'due' ? 'text-rose-700 font-semibold' : 'text-slate-500')}>
                    {w.kind === 'reply' ? 'No reply yet' : 'Reminder'} · {w.status === 'due' ? 'due now' : `due ${listTime(w.due_at)}`}{w.note ? ` · ${w.note}` : ''}
                  </span>
                </button>
                <Button size="sm" variant="secondary" icon={Check} onClick={() => done(w.id)}>Done</Button>
              </li>
            ))}
          </ul>
        ) : <Empty>Nothing. On any message choose Remind me, or after sending, Watch for a reply.</Empty>}
      </div>
      <div>
        <p className="text-[13.5px] font-semibold text-slate-800 mb-2">Waiting to send</p>
        {outbox.length ? (
          <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
            {outbox.map((o) => (
              <li key={o.id} className="flex items-center gap-3 px-3 py-2.5">
                <Send size={15} className={o.status === 'failed' ? 'text-rose-600' : 'text-slate-400'} aria-hidden />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] text-slate-800 truncate">{(o.payload && o.payload.subject) || '(no subject)'}</span>
                  <span className={clsx('block text-[11.5px]', o.status === 'failed' ? 'text-rose-700' : 'text-slate-500')}>{o.status === 'failed' ? `Could not send: ${o.error}` : `Sends ${fullTime(o.send_at)}`}</span>
                </span>
                {o.status === 'queued' && <Button size="sm" variant="secondary" onClick={() => cancel(o.id)}>Don't send</Button>}
              </li>
            ))}
          </ul>
        ) : <Empty>Nothing waiting. Use the arrow next to Send to send later.</Empty>}
      </div>
    </div>
  );
}

// ── Compose ──────────────────────────────────────────────────────────────────────────────────
function ComposeSection({ prefs, set, accounts }) {
  const c = prefs.compose;
  const mine = (accounts || []).filter((a) => a.can_act);
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-3 py-3.5 border-b border-slate-100">
        <Field label="Font"><Select value={c.font} onChange={(v) => set({ compose: { ...c, font: v } })} options={['Segoe UI', 'Arial', 'Calibri', 'Georgia', 'Verdana', 'Times New Roman', 'Tahoma'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Size"><Select value={String(c.fontSize)} onChange={(v) => set({ compose: { ...c, fontSize: Number(v) } })} options={[12, 13, 14, 15, 16, 18].map((x) => ({ value: String(x), label: `${x} px` }))} /></Field>
      </div>
      <p className="text-[13px] py-3 border-b border-slate-100" style={{ fontFamily: c.font, fontSize: c.fontSize }}>This is how new mail will look.</p>
      {mine.length > 1 && (
        <div className="py-3.5 border-b border-slate-100">
          <Field label="Send new mail from" hint="Used when you are looking at all your mail. Inside one mailbox’s folders, new mail goes from that mailbox."><Select value={c.defaultAccount || ''} allowEmpty emptyLabel="The mailbox I am looking at" onChange={(v) => set({ compose: { ...c, defaultAccount: v || null } })} options={mine.map((a) => ({ value: a.id, label: a.email }))} /></Field>
        </div>
      )}
      <Toggle label="Always show Cc and Bcc" checked={c.alwaysCcBcc} onChange={(v) => set({ compose: { ...c, alwaysCcBcc: v } })} />
    </>
  );
}

// ── Signatures, templates, quick texts ───────────────────────────────────────────────────────
const KIND = {
  signature: { one: 'signature', intro: 'Make as many signatures as you like, then choose which one each mailbox adds to new mail and to replies.' },
  template: { one: 'template', intro: 'Whole messages you send often (with a subject). Start a new mail from one with Templates in the compose bar.' },
  quicktext: { one: 'quick text', intro: 'Short pieces of text you type often (an address, a standard paragraph). Insert one where you are typing with Quick text in the compose bar.' },
};
function SnippetsSection({ kind, prefs, set, accounts }) {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/mail/snippets', { params: { kind } }).then((r) => setList(r.data || [])).catch(() => setList([])), [kind]);
  useEffect(() => { load(); setForm(null); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { kind, name: form.name, subject: form.subject, html: form.html, shared: !!form.shared };
      let id = form.id;
      if (form.id) await api.put(`/mail/snippets/${form.id}`, body); else id = (await api.post('/mail/snippets', body)).data.id;
      // Brought in from a mailbox: that mailbox uses it for new mail and replies.
      if (form.forAccount && id) set({ signatures: { ...(prefs.signatures || {}), [form.forAccount]: { newId: id, replyId: id } } });
      toast.success(form.forAccount ? 'Saved, and set for that mailbox' : 'Saved'); setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (s) => { if (!window.confirm(`Remove "${s.name}"?`)) return; try { await api.delete(`/mail/snippets/${s.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const mine = (accounts || []).filter((a) => a.can_act);
  const sigs = kind === 'signature' ? (list || []) : [];
  const sigFor = (accId, which) => ((prefs.signatures || {})[accId] || {})[which] || '';
  const setSig = (accId, which, v) => set({ signatures: { ...(prefs.signatures || {}), [accId]: { ...((prefs.signatures || {})[accId] || {}), [which]: v || null } } });
  const [importing, setImporting] = useState(null);
  const importFrom = async (a) => {
    setImporting(a.id);
    try {
      const r = await api.post(`/mail/accounts/${a.id}/import-signature`);
      setForm({ name: r.data.name, html: r.data.html, subject: '', shared: false, forAccount: a.id, found: r.data.how });
    } catch (e) { toast.error(errMsg(e)); } finally { setImporting(null); }
  };
  return (
    <>
      <Note>{KIND[kind].intro}</Note>
      {kind === 'signature' && mine.length > 0 && (
        <div className="mt-4 rounded-xl ring-1 ring-sky-200 bg-sky-50/50 p-3.5">
          <p className="text-[13px] font-semibold text-slate-800">Bring in your signature from Outlook or Gmail</p>
          <p className="text-[12.5px] text-slate-600 mt-0.5">Microsoft and Google do not let other apps read saved signatures, so Rostery takes it from your recent sent emails (the part they all end with).</p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {mine.map((a) => <Button key={a.id} size="sm" variant="secondary" loading={importing === a.id} onClick={() => importFrom(a)}>From {a.email}</Button>)}
          </div>
        </div>
      )}
      <div className="mt-4 flex justify-end"><Button size="sm" icon={Plus} onClick={() => setForm({ name: '', subject: '', html: '', shared: false })}>New {KIND[kind].one}</Button></div>
      {form && (
        <form className="mt-3 rounded-xl ring-1 ring-sky-200 bg-sky-50/40 p-3.5 space-y-3" onSubmit={(e) => { e.preventDefault(); save(); }}>
          {form.forAccount && (
            <p className="text-[12.5px] text-slate-700 bg-white rounded-lg ring-1 ring-slate-200 px-3 py-2">
              This is what was found in your sent emails. Check it, delete anything that is not part of your signature, then press <b>Use this signature</b>.
            </p>
          )}
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus maxLength={120} placeholder={kind === 'signature' ? 'Work' : kind === 'template' ? 'Plan review invitation' : 'Office address'} /></Field>
          {kind === 'template' && <Field label="Subject"><Input value={form.subject || ''} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>}
          <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden bg-white"><MailEditor value={form.html} onChange={(v) => setForm((x) => ({ ...x, html: v }))} minHeight={kind === 'quicktext' ? 90 : 150} placeholder="Write it here" /></div>
          {kind !== 'signature' && <Switch label="Share with the office" hint="Everyone in your company can use it; only you can change it." checked={!!form.shared} onChange={(v) => setForm({ ...form, shared: v })} />}
          <div className="flex gap-2 justify-end"><Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button><Button type="submit" loading={busy}>{form.forAccount ? 'Use this signature' : 'Save'}</Button></div>
        </form>
      )}
      {!list ? <Loader2 size={18} className="animate-spin text-slate-400 mt-4" aria-label="Loading" /> : list.length ? (
        <ul className="mt-3 rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
          {list.map((s) => (
            <li key={s.id} className="flex items-start gap-3 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-slate-800">{s.name}{s.shared && <span className="ml-2 text-[11px] font-medium text-[#0f6cbd]">Shared</span>}{!s.mine && <span className="ml-2 text-[11px] font-medium text-slate-500">From a colleague</span>}</p>
                {s.subject && <p className="text-[12px] text-slate-500 truncate">Subject: {s.subject}</p>}
                <p className="text-[12px] text-slate-500 line-clamp-2">{String(s.html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}</p>
              </div>
              {s.mine && <><IconAct label="Change" icon={Pencil} onClick={() => setForm({ ...s })} /><IconAct label="Remove" icon={Trash2} danger onClick={() => remove(s)} /></>}
            </li>
          ))}
        </ul>
      ) : !form && <Empty>None yet.</Empty>}
      {kind === 'signature' && sigs.length > 0 && mine.length > 0 && (
        <div className="mt-6">
          <p className="text-[13.5px] font-semibold text-slate-800 mb-2">Which signature each mailbox uses</p>
          <div className="space-y-3">
            {mine.map((a) => (
              <div key={a.id} className="rounded-xl ring-1 ring-slate-200 p-3">
                <p className="text-[13px] font-semibold text-slate-700 mb-2 truncate">{a.email}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="New mail"><Select value={sigFor(a.id, 'newId')} allowEmpty emptyLabel={a.signature_html ? "The mailbox's own" : 'None'} onChange={(v) => setSig(a.id, 'newId', v)} options={[...(a.signature_html ? [{ value: 'none', label: 'None' }] : []), ...sigs.map((s) => ({ value: s.id, label: s.name }))]} /></Field>
                  <Field label="Replies and forwards"><Select value={sigFor(a.id, 'replyId')} allowEmpty emptyLabel={a.signature_html ? "The mailbox's own" : 'None'} onChange={(v) => setSig(a.id, 'replyId', v)} options={[...(a.signature_html ? [{ value: 'none', label: 'None' }] : []), ...sigs.map((s) => ({ value: s.id, label: s.name }))]} /></Field>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── Blocked and safe senders ─────────────────────────────────────────────────────────────────
function SendersSection() {
  const [list, setList] = useState(null);
  const [text, setText] = useState({ block: '', safe: '' });
  const load = useCallback(() => api.get('/mail/senders').then((r) => setList(r.data || [])).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const add = async (kind) => {
    try { await api.post('/mail/senders', { kind, pattern: text[kind] }); setText({ ...text, [kind]: '' }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => { await api.delete(`/mail/senders/${id}`).catch(() => {}); load(); };
  if (!list) return <Loader2 size={18} className="animate-spin text-slate-400" aria-label="Loading" />;
  return (
    <div className="space-y-6">
      {[['block', 'Blocked senders', 'New mail from these goes straight to Junk in the mailboxes you connected.'], ['safe', 'Safe senders', 'Pictures in their mail show without asking (when Privacy is set to "Show for safe senders").']].map(([kind, title, hint]) => (
        <div key={kind}>
          <p className="text-[13.5px] font-semibold text-slate-800">{title}</p>
          <p className="text-[12.5px] text-slate-500 mb-2">{hint}</p>
          <form className="flex gap-2 mb-2" onSubmit={(e) => { e.preventDefault(); add(kind); }}>
            <Input value={text[kind]} onChange={(e) => setText({ ...text, [kind]: e.target.value })} placeholder="name@example.com or example.com" aria-label={`Add to ${title}`} />
            <Button type="submit" disabled={!text[kind].trim()}>Add</Button>
          </form>
          {list.filter((r) => r.kind === kind).length ? (
            <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
              {list.filter((r) => r.kind === kind).map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-3 py-2">
                  {kind === 'block' ? <Ban size={14} className="text-rose-500" aria-hidden /> : <ShieldCheck size={14} className="text-emerald-600" aria-hidden />}
                  <span className="flex-1 text-[13px] text-slate-800">{r.pattern.startsWith('@') ? `Anyone at ${r.pattern.slice(1)}` : r.pattern}</span>
                  <IconAct label="Remove" icon={X} onClick={() => remove(r.id)} />
                </li>
              ))}
            </ul>
          ) : <p className="text-[12.5px] text-slate-400">None.</p>}
        </div>
      ))}
    </div>
  );
}

// ── Automatic replies ────────────────────────────────────────────────────────────────────────
const toLocal = (iso) => { if (!iso) return ''; const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
function AutoReplySection({ accounts, me }) {
  const mine = (accounts || []).filter((a) => a.owner_user_id === me.id || (a.visibility === 'team' && a.can_act));
  const [accId, setAccId] = useState(mine[0] ? mine[0].id : '');
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!accId) return;
    setF(null);
    api.get(`/mail/accounts/${accId}/auto-reply`).then((r) => setF({ ...r.data, start: toLocal(r.data.start) || toLocal(new Date().toISOString()), end: toLocal(r.data.end) })).catch((e) => toast.error(errMsg(e)));
  }, [accId]);
  if (!mine.length) return <Empty>Automatic replies are set on your own mailboxes. Connect one first.</Empty>;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/mail/accounts/${accId}/auto-reply`, { ...f, start: f.start ? new Date(f.start).toISOString() : null, end: f.end ? new Date(f.end).toISOString() : null });
      toast.success(r.data.enabled ? 'Automatic replies are on' : 'Automatic replies are off');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      {mine.length > 1 && <Field label="Mailbox"><Select value={accId} onChange={setAccId} options={mine.map((a) => ({ value: a.id, label: a.email }))} /></Field>}
      {!f ? <Loader2 size={18} className="animate-spin text-slate-400" aria-label="Loading" /> : (
        <>
          <Switch label="Send automatic replies" hint="Each person who writes gets one reply every 4 days, and at most 20 an hour go out. Never sent to no-reply addresses, robots or other automatic replies." checked={!!f.enabled} onChange={(v) => setF({ ...f, enabled: v })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="From"><Input type="datetime-local" value={f.start || ''} onChange={(e) => setF({ ...f, start: e.target.value })} /></Field>
            <Field label="Until" hint="Leave empty to keep it on until you turn it off."><Input type="datetime-local" value={f.end || ''} onChange={(e) => setF({ ...f, end: e.target.value })} /></Field>
          </div>
          <Switch label="Also reply to people I have never emailed" hint="Off: only people this mailbox has written to, and participants' contacts, get a reply. Leave off unless you need it; senders can be faked." checked={!!f.everyone} onChange={(v) => setF({ ...f, everyone: v })} />
          <Field label="Subject" hint='Sent as "Automatic reply: …"'><Input value={f.subject || ''} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Out of the office" /></Field>
          <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden"><MailEditor value={f.html || ''} onChange={(v) => setF((x) => ({ ...x, html: v }))} minHeight={140} placeholder="Thanks for your email. I am away until Monday 27 October. For anything urgent please call the office on 02 9000 0000." /></div>
          <Note>Rostery sends these as new mail arrives (it checks every few minutes), so they work for every kind of mailbox.</Note>
          <div className="flex justify-end"><Button onClick={save} loading={busy}>Save</Button></div>
        </>
      )}
    </div>
  );
}

// ── Mailboxes ─────────────────────────────────────────────────────────────────────────────────
function AccountsSection({ accounts, onAdd, onAccountSettings }) {
  return (
    <>
      <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
        {(accounts || []).map((a) => (
          <li key={a.id} className="flex items-center gap-3 px-3 py-3">
            <span className="w-9 h-9 rounded-full text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: a.color || avatarColor(a.email) }} aria-hidden>{initials(a.display_name, a.email)}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-semibold text-slate-800 truncate">{a.email}</p>
              <p className={clsx('text-[12px]', a.status === 'active' ? 'text-slate-500' : 'text-amber-700')}>
                {a.provider === 'microsoft' ? 'Microsoft' : a.provider === 'google' ? 'Google' : 'IMAP'} · {a.status === 'reauth' ? 'needs you to sign in again' : a.status === 'error' ? 'last check failed' : a.last_sync_at ? `checked ${listTime(a.last_sync_at)}` : 'getting mail…'}
                {!a.can_act && ' · read only'}
              </p>
            </div>
            <Button size="sm" variant="secondary" icon={Cog} onClick={() => onAccountSettings(a)}>Settings</Button>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button icon={Plus} onClick={onAdd}>Add a mailbox</Button>
      </div>
      <p className="text-[12.5px] text-slate-500 mt-3 flex items-center gap-1.5"><LogOut size={13} aria-hidden /> To sign out of a mailbox, open its Settings.</p>
    </>
  );
}

// ── Contacts: the people in your mail ────────────────────────────────────────────────────────
function ContactsSection({ onCompose }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get('/mail/contacts', { params: { q } }).then((r) => setList(r.data || [])).catch(() => setList([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <>
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" className="pl-8" aria-label="Search people" />
      </div>
      {!list ? <Loader2 size={18} className="animate-spin text-slate-400" aria-label="Loading" /> : list.length ? (
        <ul className="rounded-xl ring-1 ring-slate-200 divide-y divide-slate-100">
          {list.map((c) => (
            <li key={c.address} className="flex items-center gap-3 px-3 py-2.5">
              <span className="w-8 h-8 rounded-full text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: avatarColor(c.address) }} aria-hidden>{initials(c.name, c.address)}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-slate-800 truncate">{c.name || c.address}</p>
                <p className="text-[12px] text-slate-500 truncate">{c.address} · {c.messages} message{c.messages === 1 ? '' : 's'}{c.links.length ? ` · ${c.links[0].label} (${c.links[0].role})` : ''}</p>
              </div>
              <Button size="sm" variant="secondary" icon={PenSquare} onClick={() => onCompose({ to: [{ name: c.name || '', address: c.address }] })}>Email</Button>
            </li>
          ))}
        </ul>
      ) : <Empty>No one yet.</Empty>}
    </>
  );
}
